import React, { Dispatch, FC, memo, SetStateAction } from "react";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";

import { getCurrentUserCompanyName } from "Slices/currentUser";
import { useAuth } from "Components/Authentication/AuthProvider";
import {
  useAdminConsoleAccess,
  useHasAdminAccess,
} from "Components/Authentication/useRoleAccess";
import { ReactComponent as MapIcon } from "../../assets/mapIcon.svg";
import { ReactComponent as MissionsIcon } from "../../assets/missions.svg";
import { ReactComponent as AssetsIcon } from "../../assets/assetsIcon.svg";
import { ReactComponent as OTAIcon } from "../../assets/otaIcon.svg";
import { ReactComponent as PeopleIcon } from "../../assets/peopleIcon.svg";
import { ReactComponent as VCMSIcon } from "../../assets/vcmsIcon.svg";
import {
  LinksContainerStyled,
  LinksItemStyled,
  LinksItemContentStyled,
  LinksItemTextStyled,
} from "./styles";
import { FMSLogger } from "FMSLogger";
import { NAVIGATION_BAR_ACTIONS } from "Utils/constants";
import { useFlags } from "launchdarkly-react-client-sdk";

const navigationBarActionsLogger = FMSLogger.byPrefix(NAVIGATION_BAR_ACTIONS);

interface LinkItemType {
  id: string;
  text: string;
  link: string;
  Icon: React.FC<React.SVGProps<SVGSVGElement>>;
  onlyAdmin?: boolean;
}

const linksConfig: Array<LinkItemType> = [
  {
    id: "map",
    text: "Map",
    link: "/",
    Icon: MapIcon,
    onlyAdmin: true,
  },
  {
    id: "missions",
    text: "Missions",
    link: "/missions",
    Icon: MissionsIcon,
    onlyAdmin: false,
  },
  {
    id: "assets",
    text: "Assets",
    link: "/vehicles",
    Icon: AssetsIcon,
    onlyAdmin: true,
  },
  {
    id: "ota",
    text: "OTA",
    link: "/ota",
    Icon: OTAIcon,
    onlyAdmin: true,
  },
  {
    id: "people",
    text: "People",
    link: "/people",
    Icon: PeopleIcon,
    onlyAdmin: true,
  },
  {
    id: "vcms",
    text: "VCMS",
    link: "/vcms",
    Icon: VCMSIcon,
    onlyAdmin: false,
  },
];

export const LinksContainer: FC<{
  setIsSecondaryNavAssetsShown: Dispatch<SetStateAction<boolean>>;
  setIsSecondaryNavMapShown: Dispatch<SetStateAction<boolean>>;
  setIsSecondaryNavMissionsShown: Dispatch<SetStateAction<boolean>>;
  setIsSecondaryNavOTAShown: Dispatch<SetStateAction<boolean>>;
  setIsSecondaryNavVCMSShown: Dispatch<SetStateAction<boolean>>;
}> = memo(
  ({
    setIsSecondaryNavAssetsShown,
    setIsSecondaryNavMapShown,
    setIsSecondaryNavMissionsShown,
    setIsSecondaryNavOTAShown,
    setIsSecondaryNavVCMSShown,
  }) => {
    const { pathname } = useLocation();
    const isAdmin = useHasAdminAccess();
    const usesAdminConsole = useAdminConsoleAccess();
    const companyName = useSelector(getCurrentUserCompanyName);
    const { activeCompanyId } = useAuth();
    const flags = useFlags();

    const isFeatureEnabled = (id: string) => {
      // Gate specific link ids behind feature flags
      if (id === "stationview") {
        return !!flags.featureFlagStationView;
      }
      return true;
    };

    // Ensure links are rendered once (de-duplicate by id) in case of accidental duplicates
    const uniqueLinks = linksConfig.filter(
      (linkItem, idx, arr) => arr.findIndex((l) => l.id === linkItem.id) === idx
    );

    return (
      <LinksContainerStyled>
        {uniqueLinks.map(({ id, text, link: configLink, Icon, onlyAdmin }) => {
          // People lives at /admin for sessions that use the Admin Console
          // (Cyngn-Admin + ADMIN_CONSOLE_ENABLED); everyone else keeps /people.
          const link =
            id === "people" && usesAdminConsole ? "/admin" : configLink;
          const active: boolean =
            pathname === link ||
            (link === "/admin" && pathname.startsWith("/admin/"));

          if ((!isAdmin && onlyAdmin) || !(companyName || activeCompanyId)) {
            return null;
          }

          if (id === "map") {
            return (
              <LinksItemStyled
                data-test-id={id}
                key={id}
                to={link}
                $active={active}
                onClick={() => {
                  navigationBarActionsLogger.debug(
                    `go to ${link} page via navigation bar`
                  );
                  setIsSecondaryNavMapShown(false);
                }}
                onMouseEnter={() => {
                  setIsSecondaryNavMapShown(true);
                }}
                onMouseLeave={() => {
                  setIsSecondaryNavMapShown(false);
                }}
              >
                <LinksItemContentStyled $active={active}>
                  <Icon />
                  <LinksItemTextStyled>{text}</LinksItemTextStyled>
                </LinksItemContentStyled>
              </LinksItemStyled>
            );
          }

          if (!isFeatureEnabled(id)) return null;

          if (id === "assets") {
            const assetsPaths = ["/vehicles", "/vehicletypes", "/fleets"];
            const isAssetsActive = assetsPaths.includes(pathname);
            return (
              <LinksItemStyled
                data-test-id={id}
                key={id}
                to={link}
                $active={isAssetsActive}
                onClick={() => {
                  setIsSecondaryNavAssetsShown(false);
                }}
                onMouseEnter={() => {
                  setIsSecondaryNavAssetsShown(true);
                }}
                onMouseLeave={() => {
                  setIsSecondaryNavAssetsShown(false);
                }}
              >
                <LinksItemContentStyled $active={isAssetsActive}>
                  <Icon />
                  <LinksItemTextStyled>{text}</LinksItemTextStyled>
                </LinksItemContentStyled>
              </LinksItemStyled>
            );
          }

          if (id === "missions") {
            const missionsPaths = [
              "/missions",
              "/live_missions",
              "/queued_missions",
            ];
            const isMissionsActive = missionsPaths.includes(pathname);
            return (
              <LinksItemStyled
                data-test-id={id}
                key={id}
                to={link}
                $active={isMissionsActive}
                onClick={() => {
                  navigationBarActionsLogger.debug(
                    `go to ${link} page via navigation bar`
                  );
                  setIsSecondaryNavMissionsShown(false);
                }}
                onMouseEnter={() => {
                  setIsSecondaryNavMissionsShown(true);
                }}
                onMouseLeave={() => {
                  setIsSecondaryNavMissionsShown(false);
                }}
              >
                <LinksItemContentStyled $active={isMissionsActive}>
                  <Icon />
                  <LinksItemTextStyled>{text}</LinksItemTextStyled>
                </LinksItemContentStyled>
              </LinksItemStyled>
            );
          }

          if (id === "ota") {
            const otaPaths = ["/ota"];
            const isOTAActive =
              otaPaths.some((path) => pathname === path) || pathname === "/ota";
            return (
              <LinksItemStyled
                data-test-id={id}
                key={id}
                to={link}
                $active={isOTAActive}
                onClick={() => {
                  navigationBarActionsLogger.debug(
                    `go to ${link} page via navigation bar`
                  );
                  setIsSecondaryNavOTAShown(false);
                }}
                onMouseEnter={() => {
                  setIsSecondaryNavOTAShown(true);
                }}
                onMouseLeave={() => {
                  setIsSecondaryNavOTAShown(false);
                }}
              >
                <LinksItemContentStyled $active={isOTAActive}>
                  <Icon />
                  <LinksItemTextStyled>{text}</LinksItemTextStyled>
                </LinksItemContentStyled>
              </LinksItemStyled>
            );
          }

          if (id === "vcms") {
            const vcmsPaths = [
              "/vcms/bundle",
              "/vcms/vehicles",
              "/vcms/sites",
              "/vcms/vehicle_types",
              "/vcms/global",
              "/vcms/autonomy",
              "/vcms/owner",
              "/vcms/admin",
            ];
            const isVCMSActive = vcmsPaths.some((p) => pathname.startsWith(p));
            return (
              <LinksItemStyled
                data-test-id={id}
                key={id}
                to={link}
                $active={isVCMSActive}
                onClick={() => {
                  navigationBarActionsLogger.debug(
                    `go to ${link} page via navigation bar`
                  );
                  setIsSecondaryNavVCMSShown(false);
                }}
                onMouseEnter={() => setIsSecondaryNavVCMSShown(true)}
                onMouseLeave={() => setIsSecondaryNavVCMSShown(false)}
              >
                <LinksItemContentStyled $active={isVCMSActive}>
                  <Icon />
                  <LinksItemTextStyled>{text}</LinksItemTextStyled>
                </LinksItemContentStyled>
              </LinksItemStyled>
            );
          }

          return (
            <LinksItemStyled
              data-test-id={id}
              key={id}
              to={link}
              $active={active}
              onClick={() => {
                navigationBarActionsLogger.debug(
                  `go to ${link} page via navigation bar`
                );
              }}
            >
              <LinksItemContentStyled $active={active}>
                <Icon />
                <LinksItemTextStyled>{text}</LinksItemTextStyled>
              </LinksItemContentStyled>
            </LinksItemStyled>
          );
        })}
      </LinksContainerStyled>
    );
  }
);
